import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import React, { useState, useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { SpinnerContainer } from '@/components/Containers/SpinnerContainer';
import { ErrorModal } from '@/components/Elements/ErrorModal';
import { iFormContactFields } from '@/components/Form/FormContact';
import { iFormPhoneValidationFields } from '@/components/Form/FormPhoneValidation';
import { iFormTokenCheckFields } from '@/components/Form/FormTokenCheck';
import { iFormUserAccountFields } from '@/components/Form/FormUserAccount';
import { MyAccountPrivatePage } from '@/components/PrivatePages/MyAccountPrivatePage/MyAccountPrivatePage';
import {
  NOTIFICATION_CHECKOUT_REMINDER_ID,
  NOTIFICATION_NEW_FREE_REMINDER_ID,
  RUN_GTM_CALLBACKS_IN_MS,
} from '@/config';
import { useUpdateBillingDetails, BillingDetails } from '@/features/billing';
import { Country, getCountries } from '@/features/countries';
import { getMeInfo } from '@/features/me';
import {
  getMobileToken,
  MobileToken,
  useCreateMobileToken,
  useUpdateMobileToken,
} from '@/features/mobileToken';
import { getUserProducts, ResponseError, useDeleteUserPendingProduct } from '@/features/myProducts';
import { useCreateNotificationContact } from '@/features/notification';
import { Province, getProvinces } from '@/features/provinces';
import { TinDocumentType, getTinDocumentTypes } from '@/features/tinDocumentTypes';
import { useGroupBillingDetails } from '@/hooks/useGroupBillingDetails';
import { useMe } from '@/hooks/useMe';
import { useAuth } from '@/lib/auth';
import { getAllAxiosData, GetterAxios } from '@/lib/axios';
import {
  buildGTMInformationData,
  buildGTMCompletedBilling,
  sendEventGtm,
  buildGTMValidationAccount,
} from '@/lib/gtm/gtmUtils';
import { useNotificationStore } from '@/stores/notifications';
import { iAuthInfo, ProductTypes, UserProductTypes } from '@/types';
import {
  getActiveUserProduct,
  getPendingUserProduct,
  getUserCurrency,
  getUserData,
  buildProductsCheckingInStorageForPendings,
  redirectMyRate,
  isNewFreeUserFromMe,
  isAdminUserFromMe,
} from '@/utils/helpers';
import storage from '@/utils/storage';

export const MyAccountPage = () => {
  const handleError = useErrorHandler();
  const { getAuthInfo, newLogin } = useAuth();
  const { dismissNotification } = useNotificationStore();
  const { getMe, setMe, isMeSet } = useMe();
  const { setBillingDetails, getBillingDetails } = useGroupBillingDetails();
  const authInfo: iAuthInfo = getAuthInfo();
  const groupId = authInfo.getGroupId ? authInfo.getGroupId() : '';
  const [countries, setCountries] = useState<Country[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [tinDocumentTypes, setTinDocumentTypes] = useState<TinDocumentType[]>([]);
  const [userProducts, setUserProducts] = useState<UserProductTypes[] | null>(null);
  const [hasBeenCanceled, setHasBeenCanceled] = useState<boolean>(false);
  const [disablePage, setDisablePage] = useState<boolean>(false);
  const [mobileToken, setMobileToken] = useState<MobileToken>();
  const [statusDeleteUserPendingProductError, setStatusDeleteUserPendingProductError] = useState({
    status: 0,
    detail: '',
  });

  const createNotificationContactMutation = useCreateNotificationContact();
  const updateBillingDetailsMutation = useUpdateBillingDetails();
  const deleteUserPendingProductMutation = useDeleteUserPendingProduct();
  const createMobileTokenMutation = useCreateMobileToken();
  const updateUpdateMobileTokenMutation = useUpdateMobileToken();
  const sendDataToGTM = useGTMDispatch();
  const { i18n } = useTranslation();

  const billingDetails = getBillingDetails();

  useEffect(() => {
    if (billingDetails) {
      const getters: GetterAxios[] = [
        { callback: getUserProducts, parameters: { groupId: groupId } },
        { callback: getCountries },
        { callback: getProvinces, parameters: { countryId: billingDetails.countryId } },
        { callback: getTinDocumentTypes, parameters: { countryId: billingDetails.countryId } },
        { callback: getMobileToken, parameters: { groupId: groupId } },
      ];
      const setters: any = [
        setUserProducts,
        setCountries,
        setProvinces,
        setTinDocumentTypes,
        setMobileToken,
      ];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [billingDetails, groupId, handleError]);

  useEffect(() => {
    if (hasBeenCanceled) {
      const getters: GetterAxios[] = [
        { callback: getUserProducts, parameters: { groupId: groupId } },
      ];
      const setters: any = [setUserProducts];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [groupId, handleError, hasBeenCanceled]);

  if (!mobileToken || !isMeSet()) {
    return <SpinnerContainer />;
  }

  const countryOnChange = (formValues: any, countryId: string) => {
    getProvinces({ countryId }).then((responseProvinces) => {
      const newBillingDetails = { ...billingDetails, ...formValues.values };
      newBillingDetails.countryId = countryId;
      if (
        !newBillingDetails?.companyCif ||
        !newBillingDetails?.companyCifDocumentType ||
        !newBillingDetails?.alreadySaved
      ) {
        newBillingDetails.alreadySaved = false;
      }
      getTinDocumentTypes({ countryId }).then((responseTinDocumentTypes) => {
        setBillingDetails(newBillingDetails);
        setProvinces(responseProvinces);
        setTinDocumentTypes(responseTinDocumentTypes);
      });
    });
  };

  const buildNewBillingDetails = (
    values: iFormUserAccountFields,
    billingDetails: BillingDetails
  ) => {
    if (!values.companyCif) {
      values.companyCif = billingDetails.companyCif;
    }

    return Object.assign({} as BillingDetails, values);
  };

  const onSubmitFormUserAccount = async (values: iFormUserAccountFields): Promise<boolean> => {
    dismissNotification(NOTIFICATION_CHECKOUT_REMINDER_ID);
    setDisablePage(true);

    const updateBillingDetails = await updateBillingDetailsMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });

    if (updateBillingDetails.hasError) {
      setDisablePage(false);
      return false;
    }

    if (updateBillingDetails) {
      setBillingDetails(buildNewBillingDetails(values, billingDetails));
    }

    if (isNewFreeUserFromMe(getMe())) {
      !values.createdAt && sendEventGtm(buildGTMCompletedBilling(getMe()), sendDataToGTM);
      dismissNotification(NOTIFICATION_NEW_FREE_REMINDER_ID);
    }

    storage.clearUserCurrency();

    await newLogin(async () => true);
    await loadMe();
    if (isAdminUserFromMe(getMe()) && !values.createdAt) {
      sendEventGtm(buildGTMValidationAccount(getMe()), sendDataToGTM);
    }
    setTimeout(() => {
      redirectMyRate();
    }, RUN_GTM_CALLBACKS_IN_MS);
    return true;
  };

  const loadMe = async () => {
    const me = await getMeInfo();
    setMe(me);
    return true;
  };

  const onSubmitFormPhoneValidation = async (values: iFormPhoneValidationFields) => {
    const mobileToken = await createMobileTokenMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });
    setMobileToken(mobileToken);
  };

  const onSubmitFormTokenCheck = async (values: iFormTokenCheckFields) => {
    const mobileToken = await updateUpdateMobileTokenMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });

    setMobileToken(mobileToken);
    setBillingDetails({ ...billingDetails, phone: values.phone });
  };

  const onCancelButton = async (product: ProductTypes | null | undefined) => {
    if (!product) {
      return;
    }

    storage.clearUserData();

    dismissNotification(`${NOTIFICATION_CHECKOUT_REMINDER_ID}${product?.mdirectorId}`);
    await deleteUserPendingProductMutation.mutateAsync({
      mdirectorId: product.mdirectorId,
      onErrorCallback: onDeleteUserPendingProductErrorCallback,
    });
    setHasBeenCanceled(true);
  };

  const onDeleteUserPendingProductErrorCallback = ({ status, detail }: ResponseError) => {
    setStatusDeleteUserPendingProductError({
      status,
      detail,
    });
  };

  const onRequestInfoSubmit = async (values: iFormContactFields) => {
    sendEventGtm(buildGTMInformationData(), sendDataToGTM);
    await createNotificationContactMutation.mutateAsync({
      data: values,
    });
  };

  const renderStatusPaymentErrorModal = () => (
    <ErrorModal
      status={statusDeleteUserPendingProductError.status}
      detail={statusDeleteUserPendingProductError.detail}
      onRequestClose={() => {
        setStatusDeleteUserPendingProductError({ status: 0, detail: '' });
      }}
    />
  );

  userProducts && buildProductsCheckingInStorageForPendings(userProducts, storage);
  const userProduct = getPendingUserProduct(userProducts) || getActiveUserProduct(userProducts);
  const userCurrency = getUserCurrency(billingDetails, storage);
  const userData = getUserData(getMe(), i18n);

  return statusDeleteUserPendingProductError.status > 0 ? (
    renderStatusPaymentErrorModal()
  ) : (
    <MyAccountPrivatePage
      userProducts={userProducts}
      userData={userData}
      onSubmit={onSubmitFormUserAccount}
      countries={countries}
      provinces={provinces}
      tinDocumentTypes={tinDocumentTypes}
      countryOnChange={countryOnChange}
      userProduct={userProduct}
      groupId={groupId}
      userCurrency={userCurrency}
      onSubmitFormPhoneValidation={onSubmitFormPhoneValidation}
      onSubmitFormTokenCheck={onSubmitFormTokenCheck}
      onCancelButton={onCancelButton}
      onRequestInfoSubmit={onRequestInfoSubmit}
      mobileToken={mobileToken}
      disablePage={disablePage}
    />
  );
};
